import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useJobCommsWebSocket } from "@/contexts/JobCommsWebSocketContext";
import {
  Upload, FileText, FileSpreadsheet, Image, File, Trash2, Download,
  Loader2, FolderOpen, ExternalLink, Link2, Plus, X, Check
} from "lucide-react";
import { toast } from "sonner";

// Coloured file-type icon (dark Command Deck palette)
const getFileIcon = (fileType) => {
  if (fileType === "external_link") return <Link2 className="w-5 h-5" style={{ color: "#60A5FA" }} />;
  if (fileType?.includes("pdf")) return <FileText className="w-5 h-5" style={{ color: "#F87171" }} />;
  if (fileType?.includes("word") || fileType?.includes("document")) return <FileText className="w-5 h-5" style={{ color: "#60A5FA" }} />;
  if (fileType?.includes("sheet") || fileType?.includes("excel")) return <FileSpreadsheet className="w-5 h-5" style={{ color: "#4ADE80" }} />;
  if (fileType?.includes("image")) return <Image className="w-5 h-5" style={{ color: "#60A5FA" }} />;
  return <File className="w-5 h-5" style={{ color: "rgba(255,255,255,.6)" }} />;
};

// Coloured chip background behind the file-type icon
const iconChipBg = (fileType) => {
  if (fileType?.includes("pdf")) return "rgba(185,28,28,.18)";
  if (fileType?.includes("sheet") || fileType?.includes("excel")) return "rgba(21,128,61,.18)";
  return "rgba(96,165,250,.15)";
};

// Dark form field style
const fieldStyle = { background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)", color: "#fff", colorScheme: "dark" };

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const JobCommsDocuments = ({ jobId, initialDocuments, userEmail, userId, compact = false, userRole }) => {
  const ws = useJobCommsWebSocket();
  const [documents, setDocuments] = useState(initialDocuments || []);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkTitle, setLinkTitle] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [savingLink, setSavingLink] = useState(false);
  // Notify-crew prompt after a new doc/resource is added (business owner only)
  const [notifyPrompt, setNotifyPrompt] = useState(null); // { docId, fileName } | null
  const [notifying, setNotifying] = useState(false);

  const sendNotifyCrew = useCallback(async () => {
    if (!notifyPrompt?.docId) return;
    setNotifying(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/job-comms/${jobId}/documents/${notifyPrompt.docId}/notify?user_email=${userEmail}`
      );
      const sent = res.data?.sent || 0;
      if (sent > 0) toast.success(`Crew notified (${sent} email${sent === 1 ? "" : "s"} sent)`);
      else toast.info("No hired crew to notify");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to notify crew");
    } finally {
      setNotifying(false);
      setNotifyPrompt(null);
    }
  }, [jobId, userEmail, notifyPrompt]);

  useEffect(() => {
    if (!ws) return;
    const unsub1 = ws.on("document_uploaded", (data) => {
      if (data.job_id === jobId) {
        setDocuments((prev) => {
          if (prev.find((d) => d.id === data.document.id)) return prev;
          return [data.document, ...prev];
        });
      }
    });
    const unsub2 = ws.on("document_deleted", (data) => {
      if (data.job_id === jobId) {
        setDocuments((prev) => prev.filter((d) => d.id !== data.document_id));
      }
    });
    return () => { unsub1(); unsub2(); };
  }, [ws, jobId]);

  const handleUpload = useCallback(
    async (files) => {
      if (!files || files.length === 0) return;
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is too large. Maximum size is 10MB.`);
          continue;
        }
        setUploading(true);
        try {
          const urlRes = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/job-comms/${jobId}/documents/upload-url?user_email=${userEmail}`,
            { file_name: file.name, content_type: file.type, method: "post" }
          );
          const { url, fields, object_key } = urlRes.data;
          const formData = new FormData();
          Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
          formData.append("file", file);
          await axios.post(url, formData, { headers: { "Content-Type": "multipart/form-data" } });
          const confirmRes = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/job-comms/${jobId}/documents/confirm?user_email=${userEmail}`,
            { object_key, file_name: file.name, file_size: file.size, file_type: file.type }
          );
          setDocuments((prev) => {
            if (prev.find((d) => d.id === confirmRes.data.id)) return prev;
            return [confirmRes.data, ...prev];
          });
          toast.success(`${file.name} uploaded`);
          if (userRole === "business" && confirmRes.data?.id) {
            setNotifyPrompt({ docId: confirmRes.data.id, fileName: confirmRes.data.file_name || file.name });
          }
        } catch (err) {
          console.error("Upload failed:", err);
          toast.error(`Failed to upload ${file.name}`);
        } finally {
          setUploading(false);
        }
      }
    },
    [jobId, userEmail, userRole]
  );

  const handleAddExternalLink = async () => {
    if (!linkTitle.trim() || !linkUrl.trim()) {
      toast.error("Title and URL are required");
      return;
    }
    setSavingLink(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/job-comms/${jobId}/documents/external-link?user_email=${userEmail}`,
        { title: linkTitle.trim(), url: linkUrl.trim() }
      );
      setDocuments((prev) => [res.data, ...prev]);
      toast.success("External resource added");
      setLinkTitle("");
      setLinkUrl("");
      setShowLinkForm(false);
      if (userRole === "business" && res.data?.id) {
        setNotifyPrompt({ docId: res.data.id, fileName: res.data.file_name || linkTitle.trim() });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add resource");
    } finally {
      setSavingLink(false);
    }
  };

  const handleDelete = async (docId, fileName) => {
    if (!window.confirm(`Delete "${fileName}"?`)) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/job-comms/${jobId}/documents/${docId}?user_email=${userEmail}`
      );
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
      toast.success("Deleted");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => { e.preventDefault(); setIsDragging(false); handleUpload(Array.from(e.dataTransfer.files)); };

  const isExternalLink = (doc) => doc.type === "external_link" || doc.file_type === "external_link";

  return (
    <div className="flex flex-col h-full">
      {/* Upload area - job owner only */}
      {userRole === "business" && (
        <div
          className={`mx-3 mt-3 mb-2 ${compact ? "p-3" : "p-5"} rounded-xl text-center transition-colors cursor-pointer`}
          style={{
            border: isDragging ? "1.5px dashed #28C0D0" : "1.5px dashed rgba(255,255,255,.16)",
            background: isDragging ? "rgba(40,192,208,.08)" : "transparent",
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById("doc-upload-input")?.click()}
        >
          <input
            id="doc-upload-input"
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
            className="hidden"
            onChange={(e) => handleUpload(Array.from(e.target.files))}
          />
          {uploading ? (
            <Loader2 className={`${compact ? "w-5 h-5" : "w-7 h-7"} mx-auto mb-1 animate-spin`} style={{ color: "#28C0D0" }} />
          ) : (
            <Upload className={`${compact ? "w-5 h-5" : "w-7 h-7"} mx-auto mb-1`} style={{ color: "#28C0D0" }} />
          )}
          <p className={`${compact ? "text-[11px]" : "text-xs"} font-medium`} style={{ color: "rgba(255,255,255,.6)" }}>
            {uploading ? "Uploading..." : "Drop a file or click to upload"}
          </p>
        </div>
      )}

      {/* Add External Resource Button (business owner only) */}
      {userRole === "business" && (
        <div className="px-3 mb-2">
          {!showLinkForm ? (
            <button
              onClick={() => setShowLinkForm(true)}
              className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-medium transition-colors"
              style={{ border: "1.5px dashed rgba(40,192,208,.3)", color: "#28C0D0", background: "transparent" }}
            >
              <Link2 className="w-3.5 h-3.5" />
              Add External Resource
            </button>
          ) : (
            <div className="p-3 rounded-xl" style={{ background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.08)" }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold" style={{ color: "rgba(255,255,255,.8)" }}>External Resource</span>
                <button onClick={() => { setShowLinkForm(false); setLinkTitle(""); setLinkUrl(""); }} className="p-0.5 rounded" style={{ color: "rgba(255,255,255,.5)" }}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <input
                type="text"
                value={linkTitle}
                onChange={(e) => setLinkTitle(e.target.value)}
                placeholder="Title (e.g. Runsheet)"
                className="w-full px-3 py-2 rounded-lg text-xs mb-2 focus:outline-none"
                style={fieldStyle}
              />
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="URL (e.g. https://...)"
                className="w-full px-3 py-2 rounded-lg text-xs mb-2 focus:outline-none"
                style={fieldStyle}
              />
              <button
                onClick={handleAddExternalLink}
                disabled={savingLink || !linkTitle.trim() || !linkUrl.trim()}
                className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold disabled:opacity-50 transition-colors"
                style={{ background: "#28C0D0", color: "#0C1120" }}
              >
                {savingLink ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Add Resource
              </button>
            </div>
          )}
        </div>
      )}

      {/* Document & Resource list */}
      <div className="flex-1 overflow-y-auto px-3 pb-3">
        {documents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10" style={{ color: "rgba(255,255,255,.4)" }}>
            <FolderOpen className={`${compact ? "w-8 h-8" : "w-10 h-10"} mb-2`} />
            <p className="text-xs font-medium">No documents or resources yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {documents.map((doc) => {
              const isExternal = isExternalLink(doc);
              const docUrl = isExternal ? (doc.external_url || doc.download_url) : doc.download_url;

              if (isExternal) {
                // External resource — render as a prominent button
                return (
                  <div key={doc.id} className="rounded-xl overflow-hidden" style={{ border: "1px solid rgba(96,165,250,.3)" }}>
                    <a
                      href={docUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3.5 transition-colors"
                      style={{ background: "rgba(96,165,250,.07)" }}
                    >
                      <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(96,165,250,.15)" }}>
                        <Link2 className="w-5 h-5" style={{ color: "#60A5FA" }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold truncate" style={{ color: "#60A5FA" }}>
                          {doc.file_name}
                        </p>
                        <p className="text-[10px] truncate mt-0.5" style={{ color: "rgba(255,255,255,.45)" }}>
                          {docUrl}
                        </p>
                      </div>
                      <ExternalLink className="w-4 h-4 flex-shrink-0" style={{ color: "#60A5FA" }} />
                    </a>
                    {doc.uploaded_by === userId && (
                      <div className="flex items-center justify-end px-3 py-1.5" style={{ borderTop: "1px solid rgba(96,165,250,.18)", background: "rgba(255,255,255,.02)" }}>
                        <button
                          onClick={() => handleDelete(doc.id, doc.file_name)}
                          className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors"
                          style={{ color: "rgba(255,255,255,.5)" }}
                        >
                          <Trash2 className="w-3 h-3" /> Remove
                        </button>
                      </div>
                    )}
                  </div>
                );
              }

              // Regular file document
              return (
                <div
                  key={doc.id}
                  className="rounded-xl overflow-hidden transition-all"
                  style={{ background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.08)" }}
                >
                  <a
                    href={docUrl || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3.5 cursor-pointer"
                  >
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: iconChipBg(doc.file_type) }}>
                      {getFileIcon(doc.file_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: "#fff" }}>
                        {doc.file_name}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {doc.file_size > 0 && (
                          <>
                            <span className="text-[10px]" style={{ color: "rgba(255,255,255,.45)" }}>
                              {formatFileSize(doc.file_size)}
                            </span>
                            <span className="text-[10px]" style={{ color: "rgba(255,255,255,.3)" }}>&middot;</span>
                          </>
                        )}
                        <span className="text-[10px]" style={{ color: "rgba(255,255,255,.45)" }}>
                          {doc.uploaded_by_name}
                        </span>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 flex-shrink-0" style={{ color: "rgba(255,255,255,.4)" }} />
                  </a>
                  <div className="flex items-center justify-end gap-1 px-3 py-1.5" style={{ borderTop: "1px solid rgba(255,255,255,.07)", background: "rgba(255,255,255,.02)" }}>
                    {docUrl && (
                      <a
                        href={docUrl}
                        download
                        className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors"
                        style={{ color: "rgba(255,255,255,.6)" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Download className="w-3 h-3" /> Download
                      </a>
                    )}
                    {doc.uploaded_by === userId && (
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(doc.id, doc.file_name); }}
                        className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors"
                        style={{ color: "rgba(255,255,255,.5)" }}
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Notify Crew Prompt Modal */}
      {notifyPrompt && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: "rgba(8,11,20,.72)", backdropFilter: "blur(5px)" }}>
          <div className="w-full max-w-md rounded-2xl overflow-hidden" style={{ background: "#12162A", border: "1px solid rgba(255,255,255,.12)", boxShadow: "0 40px 90px -30px rgba(0,0,0,.85)" }}>
            <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,.08)", background: "linear-gradient(120deg,#15803D,#0F766E)" }}>
              <div className="flex items-center gap-2 text-white">
                <FileText className="w-5 h-5" />
                <h3 className="font-bold">Notify Crew?</h3>
              </div>
              <button
                onClick={() => !notifying && setNotifyPrompt(null)}
                className="text-white/70 hover:text-white"
                disabled={notifying}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">
              <p className="text-sm mb-2" style={{ color: "rgba(255,255,255,.75)" }}>
                Would you like to notify the crew that a new document has been added?
              </p>
              <p className="text-xs mb-4 truncate" style={{ color: "rgba(255,255,255,.5)" }}>
                <span className="font-semibold">Document:</span> {notifyPrompt.fileName}
              </p>
              <div className="flex items-center gap-2 justify-end">
                <button
                  onClick={() => setNotifyPrompt(null)}
                  disabled={notifying}
                  className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                  style={{ background: "rgba(255,255,255,.06)", color: "rgba(255,255,255,.8)", border: "1px solid rgba(255,255,255,.1)" }}
                >
                  No, skip
                </button>
                <button
                  onClick={sendNotifyCrew}
                  disabled={notifying}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                  style={{ background: "#15803D" }}
                >
                  {notifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Yes, send email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobCommsDocuments;
